import xor from "lodash/xor";
import { useConflictResolver } from "PFApp/components/activity_form/form_selectors/skills_frameworks_selector/hooks/use_conflict_resolver";
import { usePopulateSkillsFrameworks } from "PFApp/components/activity_form/form_selectors/skills_frameworks_selector/hooks/use_populate_skills_frameworks";
import { TemplateKey } from "PFApp/constants/templates";
import { useTemplate } from "PFApp/hooks";
import { useSelectedSkillsFrameworks } from "PFApp/hooks/activity_form/use_selected_skills_frameworks";
import { RoleFormKey } from "PFApp/types/activity_form/activity_form_schema";
import { Id, Subtemplate } from "PFCore/types";
import { useEffect, useRef, useState } from "react";

import { useFormActivityCreator } from "../../../../hooks/use_form_activity_creator";

type UsePopulateOnFrameworkIdsChange = {
  value: Id[];
  roleFormKey: RoleFormKey;
  subtemplate: Subtemplate | null;
};

type UsePopulateOnFrameworkIdsChangeReturn = {
  setAppliedFrameworkId: (ids: Id[]) => void;
};

/**
 * Watches `value` (the framework IDs stored in the form field) and
 * auto-populates the row's columns whenever the IDs change
 * from an external source such as handleFill or handlePaste.
 */
export const usePopulateOnFrameworkIdsChange = ({
  value,
  roleFormKey,
  subtemplate
}: UsePopulateOnFrameworkIdsChange): UsePopulateOnFrameworkIdsChangeReturn => {
  const roleTemplate = useTemplate(TemplateKey.Role)!;
  const { selectedSkillsFrameworks, isSkillsFrameworksLoading } = useSelectedSkillsFrameworks(value);
  const activityCreator = useFormActivityCreator(roleFormKey);

  const prevAppliedIds = useRef<Id[]>(value ?? []);
  const [pendingPopulate, setPendingPopulate] = useState(false);

  const { populateSkillsFrameworksValues } = usePopulateSkillsFrameworks({
    template: roleTemplate,
    subtemplate,
    activityCreator,
    roleFormKeyPrefix: roleFormKey
  });

  const { resolvedAttributes } = useConflictResolver({
    selectedFrameworks: selectedSkillsFrameworks,
    template: roleTemplate,
    subtemplate
  });

  // Mark a populate as needed whenever the IDs change from an external source.
  useEffect(() => {
    if (xor(value, prevAppliedIds.current).length === 0 || isSkillsFrameworksLoading) {
      return;
    }

    prevAppliedIds.current = value;

    if (value.length === 0) {
      return;
    }

    setPendingPopulate(true);
  }, [value, isSkillsFrameworksLoading]);

  // Execute the populate once resolvedAttributes has been updated by useConflictResolver.
  useEffect(() => {
    if (!pendingPopulate) {
      return;
    }

    populateSkillsFrameworksValues(resolvedAttributes.skills, resolvedAttributes.customFields);
    setPendingPopulate(false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingPopulate, resolvedAttributes]);

  const setAppliedFrameworkId = (ids: Id[]) => {
    prevAppliedIds.current = ids;
  };

  return { setAppliedFrameworkId };
};
